import { CommonModule } from '@angular/common';
import {
  Component,
  computed,
  inject,
  Inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { catchError, first, map, of } from 'rxjs';
import { MaterialModule } from '../../../../../../../material/material.module';
import { ChipOption, ChipsComponent } from '../../../../../../../shared';
import { AddDamageMechanismDialogComponent } from '../../../general-tab/components/add-damage-mechanism-dialog/add-damage-mechanism-dialog.component';
import { Client, EquipmentByTagResultDto, EquipmentSource, ImportSynapseEquipmentsRequest } from '../../../../../../../api/api-clientInformation';
import { SelectionModel } from '@angular/cdk/collections';

export interface EquipmentResponse {
  count: number;
  hasMore: boolean;
  items: any[];
  cursor: string;
}

export interface ImportResponse {
  e1EquipmentDtos: any[];
  gePredixEquipmentDtos: any[];
}

@Component({
  selector: 'app-add-equipments-dialog',
  imports: [CommonModule, MaterialModule, ReactiveFormsModule],
  templateUrl: './add-equipments-dialog.component.html',
  styleUrl: './add-equipments-dialog.component.scss',
})
export class AddEquipmentsDialogComponent implements OnInit {
  private _toastr = inject(ToastrService);
  private _apiClient = inject(Client);

  selected = signal<any[]>([]);

  getEquipments = inject(MAT_DIALOG_DATA).getEquipments;
  search = inject(MAT_DIALOG_DATA).search;

  equipments: any[] = [];

  disabled = signal(false);

  equipmentOptions: ChipOption[] = [];

  compareById = (o1: any, o2: any) => o1 && o2 && o1.id === o2.id;

  values = computed(() => this.selected().filter((item) => item?.title));

  cursor?: string;

  loading = false;

  sourceControl = new FormControl<EquipmentSource>(EquipmentSource.E1);
  tagSearchControl = new FormControl<string>('');
  searchResults = signal<EquipmentByTagResultDto[]>([]);
  searchSelection = new SelectionModel<EquipmentByTagResultDto>(true, []);
  isSearching = signal(false);
  showSearchResults = signal(false);
  EquipmentSource = EquipmentSource;

  reportFacility?: string;

  searchForm = new FormGroup({
    tag: new FormControl<string | null>(null)
  });

  public get tag() {
    return this.searchForm.get('tag') as FormControl<string | null>;
  }

  constructor(
    private dialogRef: MatDialogRef<AddDamageMechanismDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    const selectedItems = data.selectedItems?.length
      ? data.selectedItems.map((item: any) => ({
        title: item.equipmentTag,
        value: item.id,
      }))
      : [];
    this.selected.set(selectedItems);

    if (data.source === 'E1') {
      this.sourceControl.setValue(EquipmentSource.E1);
    } else if (data.source === 'Predix') {
      this.sourceControl.setValue(EquipmentSource.Predix);
    }

    this.reportFacility = data.reportFacility;
  }

  ngOnInit(): void {
    // this.getData();

    this.sourceControl.valueChanges.subscribe(() => {
      this.clearSearch();
    });
  }

  removeItem(damageMechanism: any): void {
    const current = this.selected() ?? [];
    this.selected.set(current.filter((f) => f.id !== damageMechanism.id));
  }

  // onAdd() {
  //   if (this.disabled()) {
  //     return;
  //   }

  //   const selected = this.selected() ?? [];

  //   const mappedValues = selected.map((item) => ({
  //     ...item,
  //     integrityCriticality: item.integrityCriticality,
  //     assetConsequence: item.assetConsequence,
  //   }));

  //   this.dialogRef.close(mappedValues);
  // }

  onClose() {
    this.dialogRef.close();
  }

  // Not used - facility validation is done in toggleSearchSelection
  // getSelectedEquipments(selectedOptions: ChipOption[]) {
  //   const equipments = this.equipments ?? [];

  //   const selectedEquipments = equipments?.filter((item) =>
  //     selectedOptions.some((option) => option.value === item.id)
  //   );

  //   if (selectedEquipments?.length) {
  //     if (
  //       selectedEquipments.some((item) => {
  //         return (
  //           ('facility' in item &&
  //             item?.facility !== selectedEquipments[0]?.facility) ||
  //           ('area' in item && item?.area !== selectedEquipments[0]?.area)
  //         );
  //       })
  //     ) {
  //       this._toastr.error(
  //         'Please add another equipment, facility or area doesn't match'
  //       );
  //       this.disabled.set(true);
  //       return;
  //     }
  //   }

  //   this.selected.set(selectedEquipments as any[]);
  //   this.disabled.set(false);
  // }

  // loadNext() {
  //   this.getData();
  // }

  // private getData() {
  //   if (this.loading) {
  //     return;
  //   }

  //   this.loading = true;
  //   this.getEquipments(this.cursor, 50)
  //   .pipe(
  //     first(),
  //     catchError(err => {
  //       this.loading = false;
  //       return of(err);
  //     })
  //   )
  //   .subscribe((res: any) => {
  //     this.loading = false;

  //     if (res) {
  //       const items = res.items ?? [];
  //       this.cursor = res.nextCursor;
  //       this.equipments = this.equipments.length ? [...this.equipments, ...items] : items;
  //       this.equipmentOptions = this.equipments.map(
  //         (item) =>
  //           ({
  //             title: item.equipmentTag,
  //             value: item.id,
  //           } as ChipOption)
  //       );
  //     }
  //   });
  // }

  searchByTag() {
    const tagNumber = this.tag.value?.trim();
    const source = this.sourceControl.value;

    if (!tagNumber) {
      this._toastr.warning('Please enter a tag number');
      return;
    }

    if (!source) {
      this._toastr.warning('Please select a source');
      return;
    }

    this.isSearching.set(true);
    this._apiClient.searchByTagFromSynapse(tagNumber, source)
      .pipe(
        first(),
        catchError(err => {
          this.isSearching.set(false);
          this._toastr.error('Error searching equipment');
          return of([]);
        })
      )
      .subscribe((results) => {
        this.isSearching.set(false);
        this.searchResults.set(results);
        this.showSearchResults.set(true);
        this.searchSelection.clear();

        if (!results || results.length === 0) {
          this._toastr.info('No equipment found');
        }
      });
  }

  toggleSearchSelection(item: EquipmentByTagResultDto, event?: any) {
    const isSelected = this.searchSelection.isSelected(item);
    // Only validate on checking (not unchecking)
    if (!isSelected) {
      // Check against reportFacility if provided
      if (this.reportFacility && item.facility && item.facility !== this.reportFacility) {
        this._toastr.error('Please select equipment from the same facility as the report');
        // If event is provided (from template), uncheck the box
        if (event && event.source) {
          event.source.checked = false;
        }
        return;
      }
      // Check against the facility of the first selected item in the list
      const selectedItems = this.searchSelection.selected;
      if (selectedItems && selectedItems.length > 0) {
        const firstFacility = selectedItems[0]?.facility;
        if (firstFacility && item.facility && item.facility !== firstFacility) {
          this._toastr.error('Please select equipment from the same facility as the first selected item');
          if (event && event.source) {
            event.source.checked = false;
          }
          return;
        }
      }
    }
    this.searchSelection.toggle(item);
  }

  isSearchItemSelected(item: EquipmentByTagResultDto): boolean {

    return this.searchSelection.isSelected(item);
  }

  importSelected() {
    const selectedItems = this.searchSelection.selected;

    if (!selectedItems || selectedItems.length === 0) {
      this._toastr.warning('Please select at least one equipment');
      return;
    }

    const source = this.sourceControl.value ?? EquipmentSource.E1;
    const equipmentIds = selectedItems.map(item => item.id).filter(id => !!id) as string[];

    if (equipmentIds.length === 0) {
      this._toastr.warning('Selected equipment is missing an identifier');
      return;
    }

    const request = new ImportSynapseEquipmentsRequest({
      equipmentSource: source,
      equipmentIds: equipmentIds,
    });

    this.loading = true;
    this._apiClient.importFromSynapse(request)
      .pipe(
        first(),
        catchError(err => {
          this.loading = false;
          this._toastr.error('Error importing equipment');
          return of(null);
        })
      )
      .subscribe((response: ImportResponse | null) => {
        this.loading = false;

        if (response) {
          const importedEquipments = source === EquipmentSource.E1
            ? response.e1EquipmentDtos
            : response.gePredixEquipmentDtos;

          if (importedEquipments && importedEquipments.length > 0) {
            const parsedEquipments = importedEquipments.map(equipment => ({
              ...equipment,
              // facility: this.parseFacility(equipment.facility)
            }));

            this._toastr.success(`${importedEquipments.length} equipment(s) imported successfully`);
            this.dialogRef.close(parsedEquipments);
          } else {
            this._toastr.warning('No equipment imported');
          }
        }
      });
  }


  clearSearch() {
    this.tag.setValue('');
    this.searchResults.set([]);
    this.searchSelection.clear();
    this.showSearchResults.set(false);
  }
}

